/**
 * Product Heat Map Screen (Step 7)
 * Bar-style display of entropy by subdomain. Actual vs Ideal charts, RCF/ECF contributions.
 * MarketFit CTA when marketfit_deep_dive_recommended.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Typography, Spin, Progress } from 'antd';
import { Link } from 'react-router-dom';
import { getProductDomainEntropyMap } from '../../services/productDomainSessionApi';
import { ActualVsIdealChart } from '../BridgeSession/ActualVsIdealChart';
import { ProductRCFECFBars, ProductSubdomainData } from './ProductRCFECFBars';
import type { EntropyMapMetrics } from '../../types/bridgeSession';

const { Text } = Typography;

interface ProductHeatMapScreenProps {
  sessionId: string;
  onNext: () => void;
}

export const ProductHeatMapScreen: React.FC<ProductHeatMapScreenProps> = ({
  sessionId,
  onNext,
}) => {
  const [entropyMap, setEntropyMap] = useState<{
    subdomains: Record<string, { entropy_score: number }>;
    aggregate_entropy_score: number;
    highest_entropy_subdomain: string | null;
    marketfit_deep_dive_recommended?: boolean;
    hierarchy?: Array<{ domain_name: string; rollup_entropy_score?: number }>;
    metrics?: EntropyMapMetrics & { rcf_current?: number; ecf_current?: number };
    correction_factors?: { RCF: number; ECF: number };
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const map = await getProductDomainEntropyMap(sessionId);
      setEntropyMap(map ?? null);
      setLoading(false);
    };
    load();
  }, [sessionId]);

  const metrics = useMemo((): EntropyMapMetrics | null => {
    const m = entropyMap?.metrics;
    if (!m) return null;
    return {
      current_revenue: m.current_revenue ?? 0,
      current_expense: m.current_expense ?? 0,
      ideal_revenue: m.ideal_revenue ?? 0,
      ideal_expense: m.ideal_expense ?? 0,
      recoverable_revenue: m.recoverable_revenue ?? 0,
      recoverable_expense: m.recoverable_expense ?? 0,
    };
  }, [entropyMap?.metrics]);

  const scopeMap = useMemo(() => {
    const map = new Map<string, ProductSubdomainData>();
    const subdomains = entropyMap?.subdomains ?? {};
    for (const [key, data] of Object.entries(subdomains)) {
      const e = data.entropy_score ?? 0;
      const correction = Math.max(0, Math.min(1, (10 - e) / 9));
      map.set(key.toLowerCase(), {
        entropy: e,
        correction,
        label: key.charAt(0).toUpperCase() + key.slice(1),
      });
    }
    return map;
  }, [entropyMap?.subdomains]);

  if (loading) return <Spin tip="Loading entropy map..." />;

  const subdomains = entropyMap?.subdomains ?? {};
  const entries = Object.entries(subdomains);
  const highest = entropyMap?.highest_entropy_subdomain;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <Card title="Product Entropy Heat Map">
        {metrics && (metrics.current_revenue > 0 || metrics.current_expense > 0) && (
          <ActualVsIdealChart metrics={metrics} />
        )}

        {scopeMap.size > 0 && (
          <ProductRCFECFBars
            scopeMap={scopeMap}
            rcfCorrection={entropyMap?.correction_factors?.RCF}
            ecfCorrection={entropyMap?.correction_factors?.ECF}
          />
        )}

        {entries.map(([name, data]) => {
          const pct = (data.entropy_score / 10) * 100;
          const isHighest = name.toLowerCase() === highest?.toLowerCase();
          return (
            <div key={name} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text strong={isHighest}>{name.charAt(0).toUpperCase() + name.slice(1)}</Text>
                <Text>{data.entropy_score.toFixed(1)}</Text>
              </div>
              <Progress
                percent={pct}
                showInfo={false}
                strokeColor={isHighest ? '#ff4d4f' : undefined}
              />
            </div>
          );
        })}
        <div style={{ marginTop: 24 }}>
          <Text type="secondary">Overall</Text>
          <div>
            <Text strong style={{ fontSize: 20 }}>
              {entropyMap?.aggregate_entropy_score?.toFixed(1) ?? '—'}
            </Text>
          </div>
        </div>
      </Card>

      {entropyMap?.marketfit_deep_dive_recommended && (
        <Card
          title="MarketFit misalignment detected"
          style={{ marginTop: 24, borderColor: '#faad14' }}
        >
          <p>Run full MarketFit diagnostic?</p>
          <Link to="/local-bridge/marketfit/guided">
            <Button type="primary">Run MarketFit Guided Session</Button>
          </Link>
        </Card>
      )}

      <Button type="primary" onClick={onNext} style={{ marginTop: 24 }}>
        Next
      </Button>
    </div>
  );
};
